import bnbLogo from '../assets/images/phase2_live/bnb_logo.png'
import walletIcon from '../assets/images/phase2_live/icon_web3_wallet.png'
import { useBalance } from 'wagmi'
import { useWallet } from '../lib/useWallet'
import { useExchangeRate } from '../lib/useExchangeRate'

interface DepositModalProps {
  isOpen: boolean
  onClose: () => void
  amount: string
  planType: string
  onConfirm: () => void
  isProcessing?: boolean
}

export function DepositModal({ isOpen, onClose, amount, planType, onConfirm, isProcessing = false }: DepositModalProps) {
  const { address, isConnected } = useWallet()
  const { data: balance } = useBalance({
    address: address as `0x${string}`,
  })
  const { calculateUSD } = useExchangeRate()

  if (!isOpen) return null

  // Extract numeric value from amount string (e.g., "0.5 BNB" -> "0.5")
  const numericAmount = amount.replace(/[^0-9.]/g, '')
  const usdValue = calculateUSD(parseFloat(numericAmount))

  // Format address for display (show first 2 and last 4 characters)
  const formatAddress = (addr: string | undefined) => {
    if (!addr) return 'Not connected'
    return `${addr.slice(0, 4)}...${addr.slice(-4)}`
  }

  // Get user's BNB balance formatted
  const userBalance = balance ? parseFloat(balance.formatted).toFixed(4) : '0.0000'
  
  // Check if user has sufficient balance
  // const hasInsufficientBalance = balance ? parseFloat(balance.formatted) < parseFloat(numericAmount) : true
  const hasInsufficientBalance = false
  const isWalletConnected = isConnected && address

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-[#100131] bg-opacity-60"
        onClick={onClose}
      />
      
      {/* Modal Content */}
      <div className="relative w-full max-w-full bg-[#F8FAFC] rounded-t-[40px] px-[22px] pt-9 pb-8">

        <div className="flex flex-col gap-5">
          {/* Address Row */}
          <div className="flex justify-between items-center">
            <span className="font-kanit font-bold text-[#280D5E] text-sm leading-[22px]">
              Address
            </span>
            <span className="font-kanit font-medium text-[#280D5E] text-base leading-[22px] text-right">
              {formatAddress(address)}
            </span>
          </div>

          {/* Balance Row */}
          <div className="flex justify-between items-center">
            <span className="font-kanit font-bold text-[#280D5E] text-sm leading-[22px]">
              Deposit
            </span>
            <div className="flex items-center gap-1">
              <img 
                src={walletIcon} 
                alt="Wallet" 
                className="w-[14px] h-[14px] flex-shrink-0 opacity-90 mr-1"
                style={{ aspectRatio: '1/1' }}
              />
              <span className="font-kanit font-medium text-[#280D5E] text-base leading-[22px] text-right">
                {userBalance}
              </span>
            </div>
          </div>

          {/* Amount Display Card */}
          <div className="relative w-full h-[87px] bg-[#F5EEFF] border border-gradient-to-r from-[#E9DAFF] to-transparent rounded-[20px] p-[14.5px]">
            {/* BNB Logo */}
            <div className="absolute left-[14.5px] top-6 w-[41px] h-[40px] bg-[#F3BA2F] rounded-full flex items-center justify-center shadow-sm">
              <img src={bnbLogo} alt="BNB Logo" className="w-[40px] h-[40px]" />
            </div>
            
            {/* Amount */}
            <div className="absolute right-[20px] top-4">
              <div className="font-kanit font-bold text-[#280D5E] text-[25px] leading-[22px] text-right">
                {numericAmount}
              </div>
              <div className="font-kanit font-medium text-[#796EAA] text-sm leading-[22px] text-right mt-1">
                ~{usdValue} USD
              </div>
            </div>
          </div>

          {/* Subscription Type Row */}
          <div className="flex justify-between items-center">
            <span className="font-kanit font-medium text-[#796EAA] text-base leading-[22px]">
              Subscription type
            </span>
            <span className="font-kanit font-medium text-[#280D5E] text-base leading-[22px] text-right">
              {planType} Plan
            </span>
          </div>

          {/* Subscription Amount Row */}
          <div className="flex justify-between items-center">
            <span className="font-kanit font-medium text-[#796EAA] text-base leading-[22px]">
              Subscription amount
            </span>
            <span className="font-kanit font-medium text-[#280D5E] text-base leading-[22px] text-right">
              {amount}
            </span>
          </div>

          {/* Disclaimer Text */}
          <p className="font-kanit font-medium text-[#796EAA] text-[13px] leading-[22px]">
           Please review the plan details and token rewards in the FAQ before confirming your subscription.
          </p>

          {/* Confirm Button */}
          <button
            onClick={onConfirm}
            disabled={!isWalletConnected || hasInsufficientBalance || isProcessing}
            className={`w-full font-kanit font-bold text-base py-3.5 rounded-xl shadow-soft transition-colors flex items-center justify-center gap-2 ${
              !isWalletConnected || hasInsufficientBalance || isProcessing
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-[#1FC7D4] text-white hover:bg-[#1AB5C1]'
            }`}
          >
            {isProcessing && (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
            )}
            {isProcessing
              ? 'Processing...'
              : !isWalletConnected 
                ? 'Connect Wallet' 
                : hasInsufficientBalance 
                  ? 'Insufficient Balance' 
                  : 'Confirm Deposit'
            }
          </button>
        </div>
      </div>
    </div>
  )
} 